import React, { useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useIntl } from 'react-intl';

import { DeleteDialogWizard, useDialogWizardState } from '@futrli/ui-components/components/dialog-wizard';
import { useApiView } from '@futrli/views/hooks/useApiView';
import { ModifyWorkspaceView } from '@futrli/views/views/workspaces/ModifyWorkspaceView/ModifyWorkspaceView';

interface RemoveAccessDialogProps {
  isOpen: boolean;
  isWorkspaceSuperAdmin: boolean;
  onClose: () => void;
  organisationId: string;
  workspaceId: string;
}

export const RemoveAccessDialog = ({
  isOpen,
  isWorkspaceSuperAdmin,
  onClose,
  organisationId,
  workspaceId
}: RemoveAccessDialogProps) => {
  const flags = useFlags();
  const intl = useIntl();

  const view = useApiView(useMemo(() => new ModifyWorkspaceView(flags, workspaceId), [flags, workspaceId]));

  const { screen, handleAction, handleClose } = useDialogWizardState({
    isLoading: view.state.isLoading || view.state.isRemovingAccess,
    error: view.state.errorUpdating,
    onAction: () => view.removeAccess(organisationId),
    onClose
  });

  return (
    <DeleteDialogWizard
      isOpen={isOpen}
      onClose={handleClose}
      onDelete={handleAction}
      screen={screen}
      title={intl.formatMessage({
        id: 'remove_own_organisation_access_dialog.title_2',
        defaultMessage: 'Are you sure you want to remove yourself from this organisation?'
      })}
      deleteDescription={
        isWorkspaceSuperAdmin
          ? intl.formatMessage({
              id: 'remove_own_organisation_access_dialog.super_admin_description',
              defaultMessage:
                'Removing your access to this organisation will remove your access to the whole workspace - are you sure?'
            })
          : intl.formatMessage({
              id: 'remove_own_organisation_access_dialog.are_you_sure.description',
              defaultMessage: 'Are you sure you want to remove yourself from this organisation?'
            })
      }
      deleteButtonText={intl.formatMessage({
        id: 'common.remove_access',
        defaultMessage: 'Remove access'
      })}
      cancelButtonText={intl.formatMessage({
        id: 'common.cancel',
        defaultMessage: 'Cancel'
      })}
      loadingMessage={
        view.state.isLoading
          ? intl.formatMessage({
              id: 'common.loading_please_wait',
              defaultMessage: 'Loading, please wait...'
            })
          : intl.formatMessage({
              id: 'remove_own_organisation_access_dialog.removing',
              defaultMessage: 'Removing access...'
            })
      }
      successMessage={intl.formatMessage({
        id: 'remove_own_organisation_access_dialog.removed',
        defaultMessage: 'Access removed'
      })}
      errorMessage={view.state.errorUpdating?.message}
      dataTestId="remove-access-dialog"
    />
  );
};
